/* @flow */

import type { Tools } from './Common';
import { GridWidth, GridHeight, FloorHeight, HeroSize } from './Common';
import { CustomSprite, HasSprite } from './Sprite';
import GlobalManager from './GlobalManager';
import Umbrella from './Umbrella';
import Surface from './Surface';

export default class Walker extends HasSprite {
  heldUmbrella: ?Umbrella;
  umbrellas: Array<Umbrella>;
  surfaces: Array<Surface>;

  spriteStanding: CustomSprite;
  spriteRising: CustomSprite;
  spriteFalling: CustomSprite;
  spriteStandingUmbrella: CustomSprite;
  spriteRisingUmbrella: CustomSprite;
  spriteFallingUmbrella: CustomSprite;
  spriteRunning: CustomSprite;

  width = HeroSize;
  height = HeroSize;
  backwards = false;

  position = {
    x: 100, // x is center
    y: 100, // y is bottom
  };
  speed = {
    x: 0,
    y: 0,
  };
  maxSpeedWalking = 25;
  maxSpeedJumping = 60;
  maxSpeedFallingEmpty = this.maxSpeedJumping;
  maxSpeedFallingUmbrella = 10;
  speedDecay = 3;
  speedDx = this.speedDecay * 2;
  jumping = true;

  constructor(umbrellas: Array<Umbrella>, surfaces: Array<Surface>) {
    super();

    GlobalManager.canvas.subscribeHero(tools => this.draw(tools));
    GlobalManager.clock.subscribeHero(() => this.step());
    GlobalManager.input.subscribeLevelKeyDown('ArrowLeft', () => this.moveLeft());
    GlobalManager.input.subscribeLevelKeyDown('ArrowRight', () => this.moveRight());
    GlobalManager.input.subscribeLevelKeyDown('ArrowUp', () => this.openUmbrella());
    GlobalManager.input.subscribeLevelKeyDown('ArrowDown', () => this.closeUmbrella());
    GlobalManager.input.subscribeLevelKeyPress('KeyS', () => this.toggleUmbrellaHeld());
    GlobalManager.input.subscribeLevelKeyPress('KeyA', () => this.jump());
    GlobalManager.input.subscribeLevelKeyPress('KeyD', () => this.toggleUmbrellaBounce());

    this.heldUmbrella = null;
    this.umbrellas = umbrellas;
    this.surfaces = surfaces;

    const { sprite } = GlobalManager.config;
    this.spriteStanding = new CustomSprite(sprite.hero_standing);
    this.spriteRising = new CustomSprite(sprite.hero_rising);
    this.spriteFalling = new CustomSprite(sprite.hero_falling);
    this.spriteStandingUmbrella = new CustomSprite(sprite.hero_standing_umbrella);
    this.spriteRisingUmbrella = new CustomSprite(sprite.hero_rising_umbrella);
    this.spriteFallingUmbrella = new CustomSprite(sprite.hero_falling_umbrella);
    this.spriteRunning = new CustomSprite(sprite.hero_running);
  }
  jump() {
    const { maxSpeedJumping, jumping } = this;
    if (jumping === false) {
      this.speed.y = maxSpeedJumping;
      this.jumping = true;
    }
  }
  calculateUmbrellaDistance(umbrella: Umbrella) {
    // todo massage positions;
    const { bottomPosition } = umbrella;
    const thisTop = {
      x: this.position.x,
      y: this.position.y + this.height / 2,
    };
    return Math.sqrt(
      Math.pow(bottomPosition.y - thisTop.y, 2) + Math.pow(bottomPosition.x - thisTop.x, 2)
    );
  }
  toggleUmbrellaHeld() {
    if (this.heldUmbrella) {
      this.heldUmbrella.release();
      this.heldUmbrella = null;
    } else {
      const toGrab = this.umbrellas.filter(u => u.canGrab)[0];
      if (toGrab) {
        toGrab.grab();
        this.heldUmbrella = toGrab;
      }
    }
  }
  toggleUmbrellaBounce() {
    if (this.heldUmbrella) {
      this.heldUmbrella.toggleBounce();
    }
  }
  openUmbrella() {
    if (this.heldUmbrella) {
      this.heldUmbrella.open();
    }
  }
  closeUmbrella() {
    if (this.heldUmbrella) {
      this.heldUmbrella.close();
    }
  }
  moveLeft() {
    const { maxSpeedWalking, speedDx } = this;
    this.speed.x = Math.max(0 - maxSpeedWalking, this.speed.x - speedDx);
    this.backwards = true;
  }
  moveRight() {
    const { maxSpeedWalking, speedDx } = this;
    this.speed.x = Math.min(maxSpeedWalking, this.speed.x + speedDx);
    this.backwards = false;
  }
  draw(tools: Tools) {
    const { heldUmbrella } = this;
    let sprite = heldUmbrella ? this.spriteStandingUmbrella : this.spriteStanding;
    if (this.jumping) {
      if (this.speed.y > 0) {
        sprite = heldUmbrella ? this.spriteRisingUmbrella : this.spriteRising;
      } else {
        sprite = heldUmbrella ? this.spriteFallingUmbrella : this.spriteFalling;
      }
    } else if (this.speed.x !== 0) {
      sprite = this.spriteRunning;
    }
    super.draw(tools, sprite, this.position, this.backwards, this.width, this.height);
  }
  step() {
    super.step();

    const {
      maxSpeedWalking,
      maxSpeedFallingEmpty,
      maxSpeedFallingUmbrella,
      speedDecay,
      jumping,
      heldUmbrella,
    } = this;
    const maxSpeedFalling = heldUmbrella ? maxSpeedFallingUmbrella : maxSpeedFallingEmpty;
    const lastPosition = { ...this.position };
    const wasJumping = jumping;

    // apply speed
    this.position.x += this.speed.x;
    this.position.y = Math.max(FloorHeight, this.position.y + this.speed.y);

    // fix for screen dimensions
    this.position.x = Math.max(0, this.position.x);
    this.position.x = Math.min(GridWidth, this.position.x);
    this.position.y = Math.max(FloorHeight, this.position.y);
    this.position.y = Math.min(GridHeight, this.position.y);

    // decay speed
    if (this.speed.x > 0) {
      this.speed.x = Math.max(0, this.speed.x - speedDecay);
      this.speed.x = Math.min(maxSpeedWalking, this.speed.x);
    } else if (this.speed.x < 0) {
      this.speed.x = Math.min(0, this.speed.x + speedDecay);
      this.speed.x = Math.max(0 - maxSpeedWalking, this.speed.x);
    }
    this.speed.y = Math.max(0 - maxSpeedFalling, this.speed.y - speedDecay);

    // slow down horizontal speed
    if (this.position.x === 0 || this.position.x === GridWidth) {
      this.speed.x = 0;
    }

    // check for collisions, stop speed
    const standingOn = this.surfaces.reduce((arr, s) => {
      const newPosition = s.isStandingOn(lastPosition, this.position);
      if (newPosition) {
        arr.push(newPosition);
      }
      return arr;
    }, []);
    if (standingOn.length) {
      const highestY = Math.max(...standingOn);
      this.position.y = highestY;
      this.speed.y = 0 - maxSpeedFallingEmpty;
      this.jumping = false;
    } else if (this.position.y === GridHeight) {
      // hit the ceiling
      this.speed.y = 0 - speedDecay;
    } else if (!wasJumping) {
      // was not jumping but now isnt touching platform. therefore, walked off ledge
      // todo tweak on ramp, keep gliding on accident
      this.jumping = true;
    }

    // umbrella checking
    this.umbrellas.forEach(u => {
      u.canGrab = false;
    });
    if (heldUmbrella) {
      heldUmbrella.setBottomPosition({
        x: this.position.x,
        y: this.position.y + this.height / 2,
        // todo umbrella height
        // y: this.position.y,
      });
    } else {
      // highlight closestUmbrella
      const closestUmbrella = this.umbrellas.reduce((prev, u) => {
        const current = {
          distance: this.calculateUmbrellaDistance(u),
          umbrella: u,
        };
        return prev !== null && prev.distance < current.distance ? prev : current;
      }, null);
      if (closestUmbrella && closestUmbrella.distance <= this.height / 2) {
        closestUmbrella.umbrella.canGrab = true;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/Walker.js